"use client";

import { useEffect, useMemo, useState } from "react";
import type { Product } from "@/types";
import {
  GEAR_PURPOSE,
  areaSearchRequestQuery,
  searchResultKey,
  type AreaSearchResponse,
} from "@/lib/search/area-search-request";
import { gearInCategories } from "@/lib/mobility/camping-gear";

type ParamSource = URLSearchParams | { get(key: string): string | null };

/**
 * 캠핑 [장비] 탭의 "그 밖의 장비"(택배 등) 원천 — 일반 검색 결과(09-29).
 *
 * - 탭이 닫혀 있으면(`enabled` false) 아무것도 하지 않는다 — 지점 목록만 볼 때 요청 0.
 * - 페이지가 들고 있는 결과가 **지금 주소의 결과**(결과 키 일치)면 그대로 쓴다 — 요청 0.
 * - 아니면(지도 영역을 바꾸거나 지운 뒤) 그 주소의 결과를 `/api/search/area?purpose=gear`
 *   에서 한 번 받는다. 결과 키당 한 번이고, 주소가 또 바뀌면 진행 중인 요청을 끊는다.
 *   실패하면 그 키로는 다시 보내지 않는다(허브 장비는 그대로 보인다).
 *
 * 반환 `products` 는 결과 원문 — 허브 상품 빼기는 호출자(`extraGear`) 몫이다.
 */
export function useGearExtras({
  enabled,
  locale,
  region,
  searchParams,
  pageResultKey,
  pageProducts,
}: {
  enabled: boolean;
  locale: string;
  /** 검색 경로의 region 세그먼트 — 결과 키·요청에 쓴다. 없으면 페이지 결과만 쓴다. */
  region: string | undefined;
  searchParams: ParamSource;
  /** 페이지가 들고 있는 결과(`pageProducts`)가 어느 주소의 결과인지 */
  pageResultKey: string | undefined;
  pageProducts: Product[];
}): { products: Product[]; pending: boolean } {
  const urlKey = region != null ? searchResultKey(region, searchParams) : null;
  const pageFresh = urlKey === null || pageResultKey === undefined || pageResultKey === urlKey;

  const [fetched, setFetched] = useState<{ key: string; products: Product[] } | null>(
    null,
  );
  const [failedKey, setFailedKey] = useState<string | null>(null);

  const needFetch =
    enabled && !pageFresh && urlKey !== null && fetched?.key !== urlKey && failedKey !== urlKey;

  const query = useMemo(
    () =>
      needFetch && region != null
        ? areaSearchRequestQuery(locale, region, searchParams, { purpose: GEAR_PURPOSE })
        : null,
    [needFetch, locale, region, searchParams],
  );

  const categories = searchParams.get("categories");
  useEffect(() => {
    if (!query || urlKey === null) return;
    const controller = new AbortController();
    const key = urlKey;
    fetch(`/api/search/area?${query}`, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`gear search responded ${res.status}`);
        return (await res.json()) as AreaSearchResponse;
      })
      // 이 경로는 카테고리를 서버에서 거르지 않는다 — 고른 카테고리만 남긴다(페이지 결과는
      // 이미 화면에서 걸러진 것이라 그대로 쓴다)
      .then((data) =>
        setFetched({ key, products: gearInCategories(data.products, categories) }),
      )
      .catch((error: unknown) => {
        // 주소가 또 바뀌어 끊은 요청 — 실패가 아니다
        if (controller.signal.aborted) return;
        console.error("[hub] gear extras failed:", error);
        setFailedKey(key);
      });
    return () => controller.abort();
  }, [query, urlKey, categories]);

  if (!enabled) return { products: [], pending: false };
  if (pageFresh) return { products: pageProducts, pending: false };
  if (fetched && fetched.key === urlKey) return { products: fetched.products, pending: false };
  return { products: [], pending: failedKey !== urlKey };
}
